import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collectUserBackup,
  detectBackupPlatform,
  exportUserBackupCsv,
  exportUserBackupJson,
  exportUserBackupPdf,
  isBackupPlatformSupported,
  platformLabel,
} from "../../js/backup";

export default function BackupExportPanel({ session, onFlash, compact = false }) {
  const [busy, setBusy] = useState("");
  const platform = useMemo(() => detectBackupPlatform(), []);
  const supported = isBackupPlatformSupported();
  const preview = useMemo(() => {
    try {
      return session?.userId ? collectUserBackup(session) : null;
    } catch {
      return null;
    }
  }, [session]);

  async function run(kind, fn) {
    if (!session?.userId) {
      onFlash?.("Sign in to export your data.");
      return;
    }
    if (!supported) {
      onFlash?.("Backup export is supported on Windows PC and Apple Mac only.");
      return;
    }
    setBusy(kind);
    try {
      await fn(session, { usePicker: true });
      onFlash?.(`${kind.toUpperCase()} export saved. Keep a copy in a folder you control.`);
    } catch (err) {
      if (err?.name === "AbortError") onFlash?.("Export cancelled.");
      else onFlash?.(err.message || "Export failed.");
    } finally {
      setBusy("");
    }
  }

  return (
    <section className={`card backup-panel${compact ? " backup-panel--compact" : ""}`}>
      <header className="backup-panel__head">
        <div>
          <h2>Backup &amp; export</h2>
          <p className="muted">
            Download a copy of your checklist, desk settings, and messages from this device. Exports are yours to
            keep — the platform does not retain them after account closure.
          </p>
        </div>
        <p className={`backup-platform${supported ? "" : " backup-platform--warn"}`}>
          This device: <strong>{platformLabel(platform)}</strong>
          {!supported ? " — export tools are for Windows PC and Apple Mac only." : null}
        </p>
      </header>

      {!supported ? (
        <p className="confirm-banner">
          Use a Windows PC or Apple Mac with Chrome, Edge, or Safari to export. Mobile and Linux browsers are not
          supported for file backup in this release.
        </p>
      ) : null}

      <details className="backup-instructions" open={!compact}>
        <summary>How to back up your data</summary>
        <ol>
          <li>Choose a format below. <strong>JSON</strong> is the complete archive; <strong>CSV</strong> opens in Excel or Numbers; <strong>PDF</strong> is a readable summary.</li>
          <li>When prompted, save to a folder you control — Documents, Desktop, or a cloud-sync folder (OneDrive, iCloud Drive, Dropbox).</li>
          <li>Export before cancelling, suspending, replacing a device, or clearing browser storage.</li>
          <li>Restore points on this device are helpful for quick rollbacks but are limited and local. Your export is the long-term copy.</li>
          <li>Agents and brokers: also export transaction message archives from the messaging desk when a file closes.</li>
        </ol>
        <p className="muted">
          Read the <Link to="/eula">EULA</Link>, <Link to="/disclaimer">Disclaimer</Link>,{" "}
          <Link to="/terms">Terms</Link>, and <Link to="/privacy">Privacy</Link> for data ownership, supported
          systems, restore limits, and suspension rules.
        </p>
      </details>

      {preview ? (
        <dl className="backup-summary">
          <div>
            <dt>Restore points on device</dt>
            <dd>{preview.summary.restorePointCount}</dd>
          </div>
          <div>
            <dt>Documents indexed</dt>
            <dd>{preview.summary.documentCount}</dd>
          </div>
          {preview.summary.progress ? (
            <div>
              <dt>Checklist progress</dt>
              <dd>
                {preview.summary.progress.percent}% ({preview.summary.progress.done}/{preview.summary.progress.total})
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Scopes included</dt>
            <dd>{Object.keys(preview.data || {}).join(", ") || "—"}</dd>
          </div>
        </dl>
      ) : null}

      <div className="backup-actions">
        <button
          type="button"
          className="primary-btn"
          disabled={!supported || Boolean(busy)}
          onClick={() => run("json", exportUserBackupJson)}
        >
          {busy === "json" ? "Exporting JSON…" : "Export JSON (full archive)"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          disabled={!supported || Boolean(busy)}
          onClick={() => run("csv", exportUserBackupCsv)}
        >
          {busy === "csv" ? "Exporting CSV…" : "Export CSV (spreadsheet)"}
        </button>
        <button
          type="button"
          className="ghost-btn"
          disabled={!supported || Boolean(busy)}
          onClick={() => run("pdf", exportUserBackupPdf)}
        >
          {busy === "pdf" ? "Exporting PDF…" : "Export PDF (summary)"}
        </button>
      </div>

      <p className="muted backup-footnote">
        File save uses your browser&apos;s download or folder picker on supported desktops. We do not upload exports
        to our servers in this build.
      </p>
    </section>
  );
}
